/**
 * Hash-based room router for Tom's Personal Space.
 * Routes: #/ (lobby/landing), #/dating, #/companion, #/workshop, #/contact, #/feed, #/lounge, #/library, #/swl
 */

export type RoomName =
  | 'lobby'
  | 'landing'
  | 'dating'
  | 'companion'
  | 'workshop'
  | 'contact'
  | 'feed'
  | 'lounge'
  | 'library'
  | 'swl'
  | 'bingo'
  | 'review'
  | 'desk';

export interface Room {
  name: RoomName;
  mount: (container: HTMLElement) => void | Promise<void>;
  unmount: () => void;
}

const rooms = new Map<RoomName, Room>();
let currentRoom: Room | null = null;
let appContainer: HTMLElement | null = null;

// Room change listeners
type RoomChangeCallback = (room: RoomName) => void;
const roomChangeListeners: RoomChangeCallback[] = [];

export function onRoomChange(cb: RoomChangeCallback): void {
  roomChangeListeners.push(cb);
}

export function registerRoom(room: Room): void {
  rooms.set(room.name, room);
}

export function getAppContainer(): HTMLElement {
  if (!appContainer) {
    appContainer = document.getElementById('app');
    if (!appContainer) throw new Error('Missing #app container');
  }
  return appContainer;
}

export function getCurrentRoom(): RoomName | null {
  return currentRoom?.name ?? null;
}

function parseRoom(): RoomName {
  // Path-based routing: /review → reviewRoom etc. (clean URL support)
  const pathSegment = window.location.pathname.replace(/^\//, '').split('/')[0] || '';
  if (pathSegment && rooms.has(pathSegment as RoomName)) return pathSegment as RoomName;

  // Hash-based routing: #/review (legacy / SPA navigation)
  const hash = window.location.hash.replace('#/', '').split('/')[0] || '';
  if (rooms.has(hash as RoomName)) return hash as RoomName;

  // Default: first registered room (lobby for personal, landing for dating)
  const first = rooms.keys().next().value;
  return first ?? 'lobby';
}

async function navigate(): Promise<void> {
  const target = parseRoom();

  const room = rooms.get(target);
  if (!room) return;

  if (currentRoom?.name === room.name) return;

  const container = getAppContainer();

  if (currentRoom) {
    currentRoom.unmount();
  }

  container.innerHTML = '';
  container.classList.remove('room-enter');
  void container.offsetWidth; // force reflow
  container.classList.add('room-enter');

  currentRoom = room;
  await room.mount(container);

  // Notify listeners
  for (const cb of roomChangeListeners) cb(room.name);
}

export function initRouter(): void {
  window.addEventListener('hashchange', () => void navigate());
  void navigate();
}

export function navigateTo(room: RoomName): void {
  window.location.hash = `#/${room}`;
}
